import * as React from "react";
import {cx} from "emotion";

import "./map-step.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {UseState}       from "@Common/react/use-state";
import {equalDeep}      from "@Common/utils/objects";

import {GlobalResizeState} from "../../../../../../browser/global-resize-state";
import {Header}            from "../../common/header/header";
import {SelectionsSection} from "./selections-section/selections-section"
import {StreetMapSection}  from "./street-map-section/street-map-section";

export const MapStep = ({state, next}) => cs(
    ["viewSize", (_, next) => GlobalResizeState({
        translate: ({width, height}) => height < 900 ? {
            size: "md",
            showFeedback: width >= 480 && window.document.getElementById('hj-survey-toggle-1'),
        } : {
            size: "lg",
            showFeedback: width >= 480 && window.document.getElementById('hj-survey-toggle-1'),
        },
        next,
    })],
    ["selecteds", (_, next) => UseState({next})],
    ["selectionsSection", ({selecteds, viewSize}, next) => SelectionsSection({
        poRoutes: state.value?.allRoutes.filter(r => r.route_type === "PO Boxes"),
        searchRequest: {
            selections: state.value?.searchRequest,
            demographics: state.value?.demographics,
        },
        selecteds,
        viewSize: viewSize.size,
        next
    })],
    ["streetMapSection", ({selecteds, selectionsSection, viewSize}, next) => StreetMapSection({
        routes: state?.value?.allRoutes,
        selecteds,
        openPoBoxTab: selectionsSection.openPoBoxTab,
        viewSize: viewSize.size,
        next
    })],
    ["done", (_, next) => UseState({
        initValue: false,
        next,
    })],
    consumeContext("apis"),
    consumeContext("neighborhoodPolygons"),
    ({selecteds, selectionsSection, streetMapSection, done, apis, neighborhoodPolygons, viewSize}) => next({
        done: done.value,
        prepareSelecteds: ({selectedRoutes}) => selecteds.onChange(selectedRoutes),
        render: ({onCancel, cancelLabel, onNext}) => cs(
            () => (
                <div className="map-step-6y7">
                    {Header({
                        icon: require("../../../../mailing-icons/listtype-eddm.png"),
                        title: "Neighborhood Search",
                        subTitle: "Reach every residence in your selected neighborhoods.",
                    })}

                    <div className="content">
                        {selectionsSection.render({
                            onApply: async () => {
                                await PrepareRoutes({
                                    searchRequest: selectionsSection.getSearchRequest(),
                                    neighborhoodPolygons,
                                    demographics: selectionsSection.demographics,
                                    state,
                                    renderErrorNoti: streetMapSection.renderErrorNoti,
                                    disabledMapState: streetMapSection.disabled,
                                    apis
                                });
                            },
                        })}
                        {streetMapSection.render()}
                    </div>

                    <div className="footer">
                        {viewSize.showFeedback && <a {...{
                            className: "feedback",
                            onClick: () => {
                                // @ts-ignore for warning against missing click function
                                window.document.getElementById('hj-survey-toggle-1')?.click();
                            },
                            "xd-qa-type": "link",
                            "xd-qa-name": "Neighborhood Search Feedback"
                        }}>
                            Have feedback?
                        </a>}
                        {cancelLabel && (
                            <button {...{
                                className: "cancel",
                                onClick: onCancel,
                                "xd-qa-type": "button",
                                "xd-qa-name": "Neighborhood Search " + cancelLabel,
                            }}>
                                {cancelLabel}
                            </button>
                        )}
                        <button {...{
                            className: cx("next", {disabled: !state.value}),
                            onClick: () => {
                                if (state.value) {
                                    done.onChange(true);
                                    onNext({unselectedRoutes: streetMapSection.getUnselecteds()});
                                }
                            },
                            "xd-qa-type": "button",
                            "xd-qa-name": "Neighborhood Search Next",
                        }}>
                            Review Selected Neighborhoods
                        </button>
                    </div>
                </div>
            ),
        ),
    })
);

const PrepareRoutes = async({searchRequest, neighborhoodPolygons, demographics, state, renderErrorNoti, disabledMapState, apis}) => {
    const zipArr = searchRequest.zipCodes ? searchRequest.zipCodes.split(',') : searchRequest.stateZipCodes.split(',');
    if (zipArr.some(zip => isNaN(zip))) {
        throw "Please enter valid 5 digit US zip code(s) only."
    }

    const searchRequestEqualDeep = equalDeep(state.value?.searchRequest, searchRequest);
    if (searchRequestEqualDeep && equalDeep(state.value?.demographics, demographics)) {
        return;
    }
    const polygonZips = searchRequest.zipCodes ? (
        searchRequest.zipCodes.split(',').filter(zip => !Object.keys(neighborhoodPolygons.value).some(pZip => zip === pZip))
    ) : (
        searchRequest.stateZipCodes.split(',').filter(zip => !Object.keys(neighborhoodPolygons.value).some(pZip => zip === pZip))
    );

    const search = (searchRequestEqualDeep && !disabledMapState.value) ? (
        state.value 
    ) : (
        await apis.next.mailing.eddm.searchEddmList({
            search: {
                ...searchRequest,
                polygonZips,
            },
            isMapBased: true})
    );
    if (!search?.warningMsg) {
        disabledMapState.onChange(false);
    }

    const polygons = {
        ...neighborhoodPolygons.value,
        ...search.allRoutes.reduce((newPolygons, r) => {
            if (polygonZips.includes(''+r.zip_code) && r.polygon) {
                newPolygons = {
                    ...newPolygons,
                    [''+r.zip_code]: {
                        ...newPolygons[''+r.zip_code],
                        [r.route]: {
                            polygon: r.polygon,
                            route_type: r.route_type,
                        }
                    }
                }
            }
            return newPolygons;
        }, {})
    };
    neighborhoodPolygons.onChange(polygons);
    
    const carrierRoutes = search.allRoutes.map(r => r.zip_code + r.route);
    const demographicKeys = {
        ...getCheckboxDemographicKeys({demographics, demographicTypes: [
            "OWNER_RENTER_FLAG",
            "GENDER_CODE",
            "DWELLING_UNIT_SIZE"
        ]}),
        ...getRangeDemographicsKeys({demographics, demographicTypes: [
            "AGE_BAND",
            "EST_INCOME_NARROW_RANGE",
            "HOME_MARKET_VALUE",
            "PR_NETWORTH",
            "LENGTH_RESIDENCE"
        ]})
    };
    const matchRates = await apis.next.mailing.eddm.getCarrierRouteDemographics({carrierRoutes, demographicKeys});
    // Map warning takes priority over match rate error
    if (search?.warningMsg) {
        renderErrorNoti({message: search?.warningMsg, color: "orange", duration: 20000});
        disabledMapState.onChange(true);
    } else if (matchRates.result === "ERROR") {
        renderErrorNoti({
            message: "Failed to retrieve demographic match scores for your neighborhood routes. Please refresh and try again or proceed without match rate scores.",
            color: "orange",
            duration: 15000
        })
    } else if (search.allRoutes.some(r => matchRates[r.zip_code + '-' + r.route] === undefined)) {
        renderErrorNoti({
            message: "Demographic match rate scores are unavailable for some neighborhood routes within your search.",
            color: "gray",
            duration: 10000
        })
    }

    state.change((_) => ({
        allRoutes: search.allRoutes.map(r => ({
            ...r,
            ...(polygons?.[''+r.zip_code]?.[r.route] ?? {}),
            match_rate: matchRates[r.zip_code + '-' + r.route],
        })),
        searchRequest,
        demographics,
        searchRequestServerKey: search.searchRequestServerKey,
    }));
};

const getCheckboxDemographicKeys = ({demographics, demographicTypes}) => (
    demographicTypes.reduce((demographicKeys, type) => {
        const typeKeys = Object.keys(demographics[type]);
        const selectedKeys = typeKeys.filter(key => demographics[type+'_'+demographics[type][key]]);
        return {
            ...demographicKeys,
            [type]: {
                keys: selectedKeys,
                isDefaultFilter: !selectedKeys.length || selectedKeys.length === typeKeys.length,
            },
        };
    }, {})
);

const getRangeDemographicsKeys = ({demographics, demographicTypes}) => (
    demographicTypes.reduce((demographicKeys, type) => {
        const typeKeys = [...demographics[type].low.keys()].filter(i => (
            demographics[type].low[i] >= demographics[type + "_low"] &&
            demographics[type].high[i] <= demographics[type + "_high"]
        )).map(i => i+1);
        return {
            ...demographicKeys,
            [type]: {
                keys: typeKeys,
                isDefaultFilter: typeKeys.length === demographics[type].low.length,
            },
        };
    }, {})
);